import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ARProductViewer from "../components/ar/ARProductViewer";
import PublicARExperience from "../components/ar/PublicARExperience";
import useCanonicalArController from "../core/workspace/ar/useCanonicalArController";
import {
  getPublicReality,
  getPublicRealityProof,
  getPublicRealityScript,
  getPublicRealityArSession,
  trackRealityEvent,
} from "../api/doarPlatformApi";
import { SurfaceLoader } from "../components/feedback/DoARAsync";
import RealityHeader from "../components/reality/RealityHeader";
import RealityPlayerStage, { preferredRealityMode } from "../components/reality/RealityPlayerStage";
import RealityProofPanel from "../components/reality/RealityProofPanel";
import RealityToDoARCollapse from "../components/reality/RealityToDoARCollapse";
import useRealityScriptRuntime from "../core/reality/useRealityScriptRuntime";
import { createCurtainSimulationController } from "../core/reality/curtainSimulationRuntime";
import useRealityStreamSession from "../core/reality/useRealityStreamSession";
import { publicRealityToToDoARContext } from "../products/todoar/toDoARJourneyContext";
import "./Reality.css";

function formatDimensions(value) {
  if (!value) return null;
  const unit = value.units || "m";
  const parts = [value.width, value.height, value.depth].filter(item => Number.isFinite(Number(item)));
  if (!parts.length) return null;
  return `${parts.map(item => Number(item).toFixed(Number(item) < 1 ? 2 : 1).replace(/\.0$/, "")).join(" × ")} ${unit}`;
}

function sourceLabel(value) {
  if (value === "merchant") return "Merchant supplied";
  if (value === "external_source") return "External product source";
  if (value === "legacy") return "Migrated source";
  return "Creator supplied";
}

export default function RealityPage() {
  const { slug } = useParams();
  const publicArRef = useRef(null);
  const todoarCollapseRef = useRef(null);
  const funnelRef = useRef(new Set());
  const [reality, setReality] = useState(null);
  const [error, setError] = useState("");
  const [shareStatus, setShareStatus] = useState('');
  const [arSession, setArSession] = useState(null);
  const [arPreparing, setArPreparing] = useState(false);
  const [mode, setMode] = useState("image");
  const modeWasSelectedRef = useRef(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [proof, setProof] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [proofError, setProofError] = useState("");
  const [script, setScript] = useState(null);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState("");
  const pendingScriptPlayRef = useRef(false);
  const requestedLiveCode = new URLSearchParams(window.location.search).get("live");
  const [liveCode, setLiveCode] = useState(requestedLiveCode || null);

  const trackOnce = useCallback((eventType) => {
    if (!slug || funnelRef.current.has(eventType)) return;
    funnelRef.current.add(eventType);
    trackRealityEvent(slug, eventType);
  }, [slug]);

  useEffect(() => {
    document.documentElement.dataset.doarSurface = "reality";
    return () => { delete document.documentElement.dataset.doarSurface; };
  }, []);

  useEffect(() => {
    let live = true;
    funnelRef.current = new Set();
    modeWasSelectedRef.current = false;
    setError("");
    setReality(null);
    setShareStatus('');
    setArSession(null);
    setArPreparing(false);
    setProofOpen(false);
    setProof(null);
    setProofError("");
    setScript(null);
    setScriptLoading(false);
    setScriptError("");
    pendingScriptPlayRef.current = false;
    setLiveCode(requestedLiveCode || null);

    getPublicReality(slug)
      .then(({ reality: next }) => {
        if (!live) return;
        setReality(next);
        if (!requestedLiveCode && next?.features?.sessionV2 && next?.live?.sessionCode) {
          setLiveCode(next.live.sessionCode);
        }
        setMode(preferredRealityMode(next));
        if (next?.identity?.title) document.title = `${next.identity.title} — DoAR Reality`;

        if (next?.ar?.sourceReady || next?.capabilities?.experience?.viewAR) {
          setArPreparing(true);
          getPublicRealityArSession(slug)
            .then(({ session }) => { if (live) setArSession(session || null); })
            .catch(() => { if (live) setArSession(null); })
            .finally(() => { if (live) setArPreparing(false); });

        }
      })
      .catch(() => { if (live) setError("This Reality is not available."); });
    return () => { live = false; };
  }, [requestedLiveCode, slug]);

  useEffect(() => {
    if (reality?.actions?.some(action => action.id === "experience-ar")) {
      trackOnce("ar_cta_shown");
    }
  }, [reality, trackOnce]);

  // Publication is available before asynchronous cinema finishes. Refresh its
  // public media projection while video is absent; leave user's chosen mode alone.
  useEffect(() => {
    if (!slug || !reality?.doarId || reality?.representations?.video) return undefined;
    let stopped = false;
    let attempts = 0;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden" || ++attempts > 24) {
        if (attempts > 24) window.clearInterval(timer);
        return;
      }
      getPublicReality(slug).then(({ reality: next }) => {
        if (stopped || !next || String(next.doarId) !== String(reality.doarId)) return;
        if (next.representations?.video) {
          setReality(next);
          if (!modeWasSelectedRef.current) setMode("video");
        }
      }).catch(() => {});
    }, 12500);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [slug, reality?.doarId, reality?.representations?.video]);

  const secondaryActions = useMemo(
    () => reality?.actions?.filter(action => action.priority === "secondary") || [],
    [reality]
  );

  const modelProduct = useMemo(() => reality ? {
    glbUrl: reality.representations?.model3d || null,
    usdzUrl: reality.representations?.iosAR || null,
    posterUrl: reality.cover?.url || null,
    placementProfile: reality.physical?.placementProfile || "floor",
    mode: "static",
    hideArButton: true,
  } : null, [reality]);

  const ar = useCanonicalArController({
    session: arSession,
    enabled: Boolean(reality?.capabilities?.experience?.viewAR),
    placementProfile: reality?.physical?.placementProfile || "floor",
    onAttempt: () => {
      trackOnce("ar_cta_clicked");
      trackOnce("ar_session_start_attempt");
    },
    onNativeLaunch: () => trackOnce("ar_launch"),
    onFailure: (err) => {
      trackOnce("ar_session_failed");
      if (err?.code === "XR_MODEL_LOAD_FAILED" || err?.code === "MODEL_REQUIRED") {
        trackOnce("ar_model_load_failed");
      }
    },
  });
  const arStillPreparing = Boolean(
    reality?.capabilities?.experience?.viewAR
    && (arPreparing || ar.preparing)
  );

  const simulationController = useMemo(() => (
    reality?.features?.simulationV1 && reality?.simulation
      ? createCurtainSimulationController(reality.simulation)
      : null
  ), [reality?.features?.simulationV1, reality?.simulation]);

  useEffect(() => () => simulationController?.reset?.(), [simulationController]);

  const live = useRealityStreamSession({
    sessionCode: liveCode,
    enabled: Boolean(reality?.features?.sessionV2 && liveCode),
    initialRole: "viewer",
  });

  useEffect(() => {
    const state = live.state;
    if (!state) return;
    if (state.player?.mode) setMode(state.player.mode);
    if (state.simulation && simulationController?.syncAuthoritativeState) {
      simulationController.syncAuthoritativeState(state.simulation);
    }
  }, [live.state, simulationController]);

  const liveSession = live.session;
  useEffect(() => {
    if (!liveSession) return;
    trackOnce("stream_joined");
  }, [liveSession, trackOnce]);

  const onSimulationAction = useCallback(
    (action) => simulationController?.applyAction?.(action) === true,
    [simulationController]
  );
  const onSimulationReset = useCallback(
    () => simulationController?.reset?.(),
    [simulationController]
  );

  const scriptRuntime = useRealityScriptRuntime({
    script,
    onModeChange: setMode,
    onSimulationAction: simulationController ? onSimulationAction : undefined,
    onSimulationReset: simulationController ? onSimulationReset : undefined,
  });

  const scriptReady = scriptRuntime.ready;
  const scriptPlay = scriptRuntime.play;
  const scriptEmitEvent = scriptRuntime.emitEvent;

  const loadScript = useCallback(async () => {
    if (!reality?.features?.scriptV1 || !reality?.script?.id) return null;
    if (script) return script;
    if (scriptLoading) return null;

    setScriptLoading(true);
    setScriptError("");
    try {
      const result = await getPublicRealityScript(slug);
      const next = result?.script || null;
      setScript(next);
      return next;
    } catch (error) {
      setScriptError(
        error?.response?.status === 404
          ? "This Reality's executable script is not available."
          : "Reality playback could not be prepared."
      );
      return null;
    } finally {
      setScriptLoading(false);
    }
  }, [reality?.features?.scriptV1, reality?.script?.id, script, scriptLoading, slug]);

  const playRealityScript = useCallback(() => {
    if (live.session && live.session.playbackState !== "ended") return;
    if (!reality?.features?.scriptV1 || !reality?.script?.publicPlayable) return;
    if (scriptReady) {
      scriptPlay();
      return;
    }
    pendingScriptPlayRef.current = true;
    loadScript();
  }, [live.session, loadScript, reality?.features?.scriptV1, reality?.script?.publicPlayable, scriptReady, scriptPlay]);

  useEffect(() => {
    if (
      live.session
      && live.session.playbackState !== "ended"
      && reality?.features?.scriptV1
      && reality?.script?.id
      && !script
      && !scriptLoading
    ) {
      loadScript();
    }
  }, [live.session, loadScript, reality?.features?.scriptV1, reality?.script?.id, script, scriptLoading]);

  useEffect(() => {
    const event = live.lastEvent;
    if (!event || event.kind !== "interaction") return;
    const interaction = event.payload?.interaction;
    if (interaction?.type === "physics.apply_impulse") {
      simulationController?.applyAction?.(interaction);
    } else if (interaction?.type === "event.emit" && scriptReady) {
      scriptEmitEvent?.(interaction.payload?.event, interaction.payload?.value ?? null);
    }
  }, [live.lastEvent, scriptReady, scriptEmitEvent, simulationController]);

  useEffect(() => {
    if (!pendingScriptPlayRef.current || !scriptReady) return;
    pendingScriptPlayRef.current = false;
    scriptPlay();
  }, [scriptReady, scriptPlay]);

  useEffect(() => {
    if (
      reality?.features?.scriptV1
      && !live.session
      && reality?.script?.autoplay === true
      && reality?.script?.publicPlayable === true
      && !script
      && !scriptLoading
    ) {
      pendingScriptPlayRef.current = true;
      loadScript();
    }
  }, [
    live.session,
    loadScript,
    reality?.features?.scriptV1,
    reality?.script?.autoplay,
    reality?.script?.publicPlayable,
    script,
    scriptLoading,
  ]);

  const startAr = ar.start;
  const startPublicAr = useCallback(() => {
    startAr(publicArRef);
  }, [startAr]);
  const openProof = useCallback(() => {
    if (!reality?.features?.proof) return;
    setProofOpen(true);
    if (proof || proofLoading) return;

    setProofLoading(true);
    setProofError("");
    getPublicRealityProof(slug)
      .then((result) => setProof(result?.proof || null))
      .catch((error) => {
        if (error?.response?.status === 404) setProofError("Public proof is not enabled for this Reality.");
        else setProofError("Proof could not be loaded right now.");
      })
      .finally(() => setProofLoading(false));
  }, [proof, proofLoading, reality?.features?.proof, slug]);


  const onArPhase = useCallback((phase) => {
    if (phase === "loading") {
      trackOnce("ar_launch");
      trackOnce("ar_session_started");
      trackOnce("ar_model_load_started");
    } else if (phase === "tracking" || phase === "targeting") {
      trackOnce("ar_model_load_succeeded");
    } else if (phase === "failed") {
      trackOnce("ar_session_failed");
    }
  }, [trackOnce]);

  const onArPlacement = useCallback(() => {
    trackOnce("ar_placement_completed");
  }, [trackOnce]);

  const merchantAction = secondaryActions.find(action => action.id === "buy" || action.id === "enquire") || null;
  const onArMerchantAction = useCallback(() => {
    trackOnce("ar_merchant_cta_clicked");
    trackRealityEvent(slug, "outbound_click");
  }, [slug, trackOnce]);



  if (error) return <main className="reality-state"><div><a href="/" className="reality-brand">DoAR</a><h1>{error}</h1><a href="/">Explore Realities →</a></div></main>;
  if (!reality) return <SurfaceLoader title="DoAR" detail="Opening Reality…" slowDetail="This Reality is taking longer to load. Retry if it does not finish." onRetry={() => window.location.reload()} homeHref="/" />;

  const primary = reality.actions?.find(action => action.priority === "primary");
  const secondary = secondaryActions;
  const dimensions = formatDimensions(reality.physical?.dimensions);
  const materials = reality.physical?.materials || [];
  const hasImage = Boolean(reality.representations?.image);
  const has3d = Boolean(reality.representations?.model3d);
  const hasVideo = Boolean(reality.representations?.video);

  const onAction = (action) => {
    if (!action || action.disabled) return;

    if (action.id === "experience-ar" || action.runtime === "public-ar" || action.runtime === "native-ar") {
      startPublicAr();
      return;
    }

    if (action.id === "play-reality" || action.runtime === "reality-script") {
      playRealityScript();
      return;
    }

    if (action.id === "continue-todoar" || action.runtime === "todoar-continuation") {
      todoarCollapseRef.current?.openFull?.();
      return;
    }

    if (!action.href) return;
    if (action.id === "buy" || action.id === "enquire") trackRealityEvent(slug, "outbound_click");
    if (action.external) window.open(action.href, "_blank", "noopener,noreferrer");
    else window.location.assign(action.href);
  };

  const shareReality = async () => {
    const liveUrl = live.session && live.session.playbackState !== "ended"
      ? `${window.location.origin}/r/${encodeURIComponent(slug)}?live=${encodeURIComponent(live.session.sessionCode)}`
      : window.location.href;
    const payload = { title: reality.identity.title, url: liveUrl };
    try {
      if (navigator.share) await navigator.share(payload);
      else await navigator.clipboard.writeText(liveUrl);
      setShareStatus('Reality link shared.');
    } catch (failure) { if (failure?.name !== 'AbortError') setShareStatus('Sharing is unavailable. Copy the page address to share this Reality.'); }
  };

  const primaryLabel = primary?.id === "experience-ar" && arStillPreparing
    ? "Preparing AR…"
    : primary?.id === "play-reality" && scriptLoading
      ? "Preparing Reality…"
      : primary?.label;
  const todoarJourneyContext = publicRealityToToDoARContext(reality);

  return (
    <RealityToDoARCollapse ref={todoarCollapseRef} journeyContext={todoarJourneyContext}>
    <main className="reality-page">
      <RealityHeader />
      {shareStatus && <p role="status">{shareStatus}</p>}

      <section className="reality-hero">
        <div className="reality-viewer" id="reality">
          {reality.features?.playerV2 ? (
            <RealityPlayerStage
              reality={reality}
              mode={mode}
              onModeChange={next => { modeWasSelectedRef.current = true; setMode(next); }}
              arRuntimeActive={ar.runtimeActive}
              scriptRuntime={scriptRuntime}
              spatialSession={arSession}
              simulationController={simulationController}
              liveState={live.state}
              liveRole={live.role}
              onLiveInteraction={live.interact}
            />
          ) : (
            <>
              <div className="reality-viewer__stage">
                {hasImage ? (
                  <img className={mode === "image" ? "is-active" : "is-hidden"} src={reality.representations.image} alt={reality.identity.title} />
                ) : null}
                {has3d && !ar.runtimeActive ? (
                  <div className={`reality-viewer__model${mode === "3d" ? " is-active" : " is-hidden"}`}>
                    <ARProductViewer product={modelProduct} arDisabled />
                  </div>
                ) : null}
                {hasVideo ? (
                  <video className={mode === "video" ? "is-active" : "is-hidden"} src={reality.representations.video} controls playsInline preload="metadata" aria-label={reality.identity.title + " video"} />
                ) : null}
                {!hasImage && !has3d && !hasVideo ? (
                  <div className="reality-viewer__fallback"><strong>DoAR</strong><span>Reality visual preparing</span></div>
                ) : null}
              </div>
              {[hasImage, has3d, hasVideo].filter(Boolean).length > 1 ? (
                <div className="reality-modebar" aria-label="Reality media">
                  {hasImage ? <button className={mode === "image" ? "active" : ""} onClick={() => setMode("image")} type="button">Image</button> : null}
                  {has3d ? <button className={mode === "3d" ? "active" : ""} onClick={() => setMode("3d")} type="button">3D</button> : null}
                  {hasVideo ? <button className={mode === "video" ? "active" : ""} onClick={() => setMode("video")} type="button">Video</button> : null}
                </div>
              ) : null}
            </>
          )}
        </div>

        <aside className="reality-summary">
          <div className="reality-summary__topline">
            <p className="reality-kicker">{reality.identity.categoryLabel}</p>
            {reality.features?.proof && reality.grounding ? (
              <button type="button" className={`reality-proof-pill reality-proof-pill--${reality.grounding.classification || "unverified"}`} onClick={openProof}>
                <strong>{reality.grounding.realityIndex == null ? "RI —" : `RI ${reality.grounding.realityIndex}`}</strong>
                <span>{reality.grounding.label || "Unverified"}</span>
              </button>
            ) : null}
          </div>
          <h1>{reality.identity.title}</h1>
          {reality.identity.brand ? <p className="reality-maker">{reality.identity.brand}</p> : null}
          {reality.identity.description ? <p className="reality-description">{reality.identity.description}</p> : null}
          {live.session ? (
            <div className={`reality-live-bar${live.session.playbackState === "ended" ? " is-ended" : ""}`} role="status">
              <span className="reality-live-dot" aria-hidden="true" />
              <div>
                <strong>{live.session.playbackState === "ended" ? "Live session ended" : "Live Reality"}</strong>
                <small>
                  {live.session.presence?.active || 0} connected · {live.connection === "live" ? "synced" : live.connection}
                </small>
              </div>
              {live.session.playbackState !== "ended" ? (
                <button
                  type="button"
                  onClick={live.role === "participant" ? live.joinAsViewer : live.joinAsParticipant}
                >
                  {live.role === "participant" ? "Watching" : "Interact"}
                </button>
              ) : null}
            </div>
          ) : null}
          {live.error ? <p className="reality-script-message is-error" role="status">{live.error}</p> : null}
          {reality.commerce?.priceLabel ? <p className="reality-price">{reality.commerce.priceLabel}</p> : null}

          <div className="reality-primary-actions">
            {primary ? (
              <button
                type="button"
                className="primary"
                onClick={() => onAction(primary)}
                disabled={
                  (primary.id === "experience-ar" && arStillPreparing)
                  || (primary.id === "play-reality" && scriptLoading)
                }
              >
                {primaryLabel}
              </button>
            ) : null}
            <div className="reality-secondary-actions">
              {secondary.map(action => <button key={action.id} type="button" onClick={() => onAction(action)}>{action.label}</button>)}
              <button type="button" onClick={shareReality}>Share</button>
            </div>
          </div>
          {ar.nativeFallback ? (
            <button type="button" className="reality-ar-fallback" onClick={() => ar.launchNative(ar.nativeFallback)}>
              Open native AR
            </button>
          ) : null}
          {ar.message ? <p className="reality-ar-message" role="status">{ar.message}</p> : null}
          {scriptLoading ? <p className="reality-script-message" role="status">Preparing executable Reality…</p> : null}
          {scriptError ? <p className="reality-script-message is-error" role="status">{scriptError}</p> : null}

          <div className="reality-quickfacts">
            {dimensions ? <span><b>Dimensions</b>{dimensions}</span> : null}
            <span><b>Scale</b>{reality.physical?.scale?.label || "Scale not verified"}</span>
            {materials.length ? <span><b>Materials</b>{materials.join(", ")}</span> : null}
            {reality.features?.proof && reality.grounding ? (
              <button type="button" className="reality-quickfacts__proof" onClick={openProof}>
                <b>Proof</b>
                <span>{Number(reality.grounding.sourceCount || 0) > 0 ? `${reality.grounding.sourceCount} independent source${reality.grounding.sourceCount === 1 ? "" : "s"}` : "Inspect grounding"}</span>
              </button>
            ) : null}
          </div>
        </aside>
      </section>

      <section className="reality-info">
        <div className="reality-info__intro">
          <p className="reality-kicker">About this Reality</p>
          <h2>One Design Object.<br />Many ways to experience it.</h2>
        </div>

        <div className="reality-info__grid">
          <article>
            <h3>Experience</h3>
            <ul>
              {reality.capabilities?.experience?.view2d ? <li>Image Reality</li> : null}
              {reality.capabilities?.experience?.view3d ? <li>Interactive 3D</li> : null}
              {reality.capabilities?.experience?.viewAR ? <li>Augmented Reality</li> : null}
              {reality.capabilities?.experience?.play ? <li>Playable Reality</li> : null}
              {reality.capabilities?.experience?.simulate ? <li>{reality.simulation?.label || "Simulation available"}</li> : null}
            </ul>
          </article>
          <article>
            <h3>Transform & use</h3>
            <ul>
              {reality.capabilities?.transform?.customize ? <li>Configurable</li> : null}
              {reality.capabilities?.transform?.remix ? <li>Remix allowed</li> : null}
              {reality.capabilities?.make?.print ? <li>Print-ready</li> : null}
              {reality.capabilities?.make?.download ? <li>Download allowed</li> : null}
              <li>Share Reality</li>
            </ul>
          </article>
          <article>
            <h3>Design Object</h3>
            <dl>
              <div><dt>DoAR</dt><dd>Design Object for Augmenting Reality</dd></div>
              <div><dt>Source</dt><dd>{sourceLabel(reality.provenance?.source)}</dd></div>
              <div><dt>Geometry</dt><dd>{String(reality.provenance?.geometry || "unknown").replace(/_/g, " ")}</dd></div>
              {reality.simulation ? <div><dt>Simulation</dt><dd>{reality.simulation.label} · {reality.simulation.disclaimer}</dd></div> : null}
              <div><dt>Rights</dt><dd>{reality.rights?.attested ? "Publication rights confirmed" : "Rights not confirmed"}</dd></div>
            </dl>
          </article>
        </div>
      </section>


      <RealityProofPanel
        open={proofOpen}
        summary={reality.grounding}
        proof={proof}
        loading={proofLoading}
        error={proofError}
        onClose={() => setProofOpen(false)}
      />


      <PublicARExperience
        ref={publicArRef}
        session={arSession}
        placementProfile={reality.physical?.placementProfile || "floor"}
        onFailure={ar.handleFailure}
        onExit={ar.handleExit}
        onPhase={onArPhase}
        onPlacement={onArPlacement}
        onAdjust={() => trackOnce("ar_adjust_used")}
        onRefineStart={() => trackOnce("ar_refine_fit_started")}
        onRefineComplete={() => trackOnce("ar_refine_fit_completed")}
        onCaptureAttempt={() => trackOnce("ar_capture_attempted")}
        onCaptureSuccess={() => trackOnce("ar_capture_succeeded")}
        onCaptureFailure={() => trackOnce("ar_capture_failed")}
        onShareAttempt={() => trackOnce("ar_share_attempted")}
        merchantAction={merchantAction}
        onMerchantAction={onArMerchantAction}
        simulationController={simulationController}
      />
    </main>
    </RealityToDoARCollapse>
  );
}
